import { Skeleton, Stack, Typography } from '@mui/material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AggregatedInfoPaper } from '@retail/price-control/components';
import { PriceControlAggregated } from '@retail/price-control/types';

interface Props {
  priceControlAggregated?: PriceControlAggregated;
  isLoading: boolean;
}

export function PriceControlAggregatedInfo({ priceControlAggregated, isLoading }: Props) {
  const t = useMyAssortmentTFunction();

  const purchasePriceChanges = priceControlAggregated?.purchasePriceAverageDiffAbsolute;
  const purchasePricePercentChange = priceControlAggregated?.purchasePriceAverageDiffPercentage;
  const salesPricePercentChange = priceControlAggregated?.salesPriceAverageDiffPercentage;
  const averageDgChangePercent = priceControlAggregated?.salesFactorAverageDiffPercentage;
  const maxDgChangePercent = priceControlAggregated?.salesFactorMaxDiffPercentage;
  return (
    <Stack flexDirection="row" width="100%" gap={2} px={3}>
      <AggregatedInfoPaper
        heading={t('myAssortment.priceControl.suppliersInLine').toUpperCase()}
        aggregatedInfo={priceControlAggregated?.suppliersInQueueAmount ?? '-'}
        aggregatedAdditionalInfo={
          <Stack flexDirection="row" alignItems="center" gap={0.5}>
            {isLoading ? (
              <Skeleton width={30} />
            ) : (
              <Typography variant="subtitle2">
                {priceControlAggregated?.totalItemsInControl ?? '-'}
              </Typography>
            )}
            <Typography variant="subtitle2" color="textSecondary">
              {t('myAssortment.priceControl.itemsInTotal')}
            </Typography>
          </Stack>
        }
        isLoadingFetchItems={isLoading}
        variant="outlined"
      />
      <AggregatedInfoPaper
        heading={t('myAssortment.priceControl.averagePurchasePriceChange').toUpperCase()}
        aggregatedInfo={`${
          purchasePricePercentChange && purchasePricePercentChange > 0 ? '+' : ''
        }${purchasePricePercentChange ?? '-'} %`}
        negativeChange={purchasePriceChanges ? negativeChange(purchasePriceChanges) : undefined}
        isLoadingFetchItems={isLoading}
        variant="outlined"
      />
      <AggregatedInfoPaper
        heading={t('myAssortment.priceControl.averageSalesPriceChange').toUpperCase()}
        aggregatedInfo={`${salesPricePercentChange && salesPricePercentChange > 0 ? '+' : ''}${
          salesPricePercentChange ?? '-'
        } %`}
        isLoadingFetchItems={isLoading}
        variant="outlined"
      />
      <AggregatedInfoPaper
        heading={t('myAssortment.priceControl.averageFactorChange').toUpperCase()}
        aggregatedInfo={`${averageDgChangePercent && averageDgChangePercent > 0 ? '+' : ''} ${
          averageDgChangePercent ?? '-'
        } %`}
        aggregatedAdditionalInfo={
          <Stack flexDirection="row" alignItems="center" gap={0.5}>
            <Typography variant="body1" color="textSecondary">
              {t('myAssortment.priceControl.highestFactorChange')}:
            </Typography>
            {isLoading ? (
              <Skeleton width={40} />
            ) : (
              <Typography variant="subtitle1">{`${maxDgChangePercent} %`}</Typography>
            )}
          </Stack>
        }
        negativeChange={averageDgChangePercent ? averageDgChangePercent <= 0 : undefined}
        isLoadingFetchItems={isLoading}
        variant="outlined"
      />
    </Stack>
  );
}

export const negativeChange = (priceChange: number) => {
  return Number(priceChange.toFixed(2)) === 0 ? undefined : priceChange > 0 ? true : false;
};
